import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Button,
  MenuItem,
  TextField,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import styles from './styles.module.css';
import DataTable from '../../utils/DataTable';
import Loader from '../../utils/Loading';
import Info from '../../utils/Alert/Info';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import DealerServices from '../../../services/DealerServices';
import WalletRechargePopup from '../../popups/WalletRechargePopup';
import { AlertMessagesContext } from 'react-alert-messages';
import { ROUTES } from '../../../const';

// Material Icons
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Normalization Helpers
function extractArray(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.results)) return res.data.results;
  return [];
}

const getDealerId = (d) => d?.id ?? d?.dealer_id;
const getDealerName = (d) => d?.name || d?.dealer_name || (d ? `Dealer #${getDealerId(d)}` : '');
const getDealerBalance = (d) =>
  parseFloat(d?.credit_balance ?? d?.wallet_balance ?? d?.balance ?? d?.current_balance ?? 0);

function DealersPage() {
  const history = useHistory();
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rechargePopupOpen, setRechargePopupOpen] = useState(false);
  const [rechargeTargetDealerId, setRechargeTargetDealerId] = useState('');

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DealerServices.getDealers();
      const list = extractArray(res);
      setDealers(list);
    } catch (error) {
      if (postAlertMessage) {
        postAlertMessage({
          text: error?.message || 'Failed to load dealers',
          type: 'failed',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [postAlertMessage]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  // Navigate to Dealer Details Page
  const handleViewDealerDetails = useCallback(
    (dealer) => {
      const id = getDealerId(dealer);
      if (id !== undefined && id !== null) {
        history.push(ROUTES.DEALER_DETAILS.replace(':dealerId', id));
      }
    },
    [history]
  );

  const handleOpenRecharge = (dealerId = '') => {
    setRechargeTargetDealerId(dealerId ? String(dealerId) : '');
    setRechargePopupOpen(true);
  };

  // Filtered Dealers
  const filteredDealers = useMemo(() => {
    return dealers.filter((d) => {
      const id = String(getDealerId(d) || '');
      const name = String(getDealerName(d) || '').toLowerCase();
      const support = String(d?.customer_support_number || d?.mobile_no || '').toLowerCase();
      const type = String(d?.type || '').toLowerCase();
      const q = searchText.trim().toLowerCase();

      // Search matching
      if (q && !id.includes(q) && !name.includes(q) && !support.includes(q) && !type.includes(q)) {
        return false;
      }

      // Status filter
      if (statusFilter === 'active' && d?.active === false) return false;
      if (statusFilter === 'inactive' && d?.active !== false) return false;

      // Type filter
      if (typeFilter !== 'all' && type !== typeFilter.toLowerCase()) return false;

      return true;
    });
  }, [dealers, searchText, statusFilter, typeFilter]);

  // Aggregated Stats
  const stats = useMemo(() => {
    const total = dealers.length;
    let activeCount = 0;
    let totalBal = 0;
    dealers.forEach((d) => {
      if (d?.active !== false) activeCount += 1;
      totalBal += getDealerBalance(d);
    });
    return {
      total,
      activeCount,
      totalBal,
    };
  }, [dealers]);

  // Pagination slice
  const paginatedDealers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDealers.slice(start, start + rowsPerPage);
  }, [filteredDealers, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Table Columns Definition
  const columns = useMemo(
    () => [
      {
        label: 'ID',
        id: 'id',
        type: 'callback',
        viewRender: (row) => (
          <span style={{ fontWeight: 600, color: '#64748b' }}>#{getDealerId(row)}</span>
        ),
      },
      {
        label: 'Dealer Name',
        id: 'name',
        type: 'callback',
        viewRender: (row) => {
          const name = getDealerName(row);
          const firstChar = (name && name[0] ? name[0] : 'D').toUpperCase();
          return (
            <div
              className={styles.dealerNameCell}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDealerDetails(row);
              }}
            >
              <div className={styles.dealerAvatar}>{firstChar}</div>
              <div>
                <span className={styles.dealerTitleText}>{name}</span>
                {row?.flavours && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{row.flavours}</div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        label: 'Type',
        id: 'type',
        type: 'callback',
        viewRender: (row) => {
          const type = row?.type || 'normal';
          const isMeezan = type.toLowerCase() === 'meezan';
          const isAdmin = type.toLowerCase() === 'admin';
          return (
            <Chip
              label={type.toUpperCase()}
              size="small"
              variant="outlined"
              color={isMeezan ? 'secondary' : isAdmin ? 'primary' : 'default'}
              style={{ fontWeight: 600, fontSize: 11 }}
            />
          );
        },
      },
      {
        label: 'Support / Phone',
        id: 'customer_support_number',
        type: 'callback',
        viewRender: (row) => row?.customer_support_number || row?.mobile_no || '-',
      },
      {
        label: 'Credit Balance',
        id: 'credit_balance',
        type: 'callback',
        viewRender: (row) => {
          const bal = getDealerBalance(row);
          const isNegative = bal < 0;
          return (
            <span
              style={{
                fontWeight: 700,
                fontSize: 14,
                color: isNegative ? '#d32f2f' : '#16a34a',
              }}
            >
              {isNegative ? '-' : ''}₹{Math.abs(bal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          );
        },
      },
      {
        label: 'Credit Limit',
        id: 'credit_limit',
        type: 'callback',
        viewRender: (row) => {
          const limit = parseFloat(row?.credit_limit || 0);
          return limit > 0
            ? `₹${limit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
            : '-';
        },
      },
      {
        label: 'Status',
        id: 'active',
        type: 'callback',
        viewRender: (row) => {
          const isActive = row?.active !== false;
          return (
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              color={isActive ? 'success' : 'error'}
              variant="filled"
              style={{
                fontWeight: 600,
                fontSize: 11,
                color: '#fff',
                backgroundColor: isActive ? '#10b981' : '#ef4444',
              }}
            />
          );
        },
      },
      {
        label: 'Online Renewal',
        id: 'is_online_renewal_enabled',
        type: 'callback',
        viewRender: (row) => {
          const enabled = row?.is_online_renewal_enabled !== false;
          return (
            <Chip
              label={enabled ? 'Enabled' : 'Disabled'}
              size="small"
              variant="outlined"
              color={enabled ? 'success' : 'default'}
              style={{ fontWeight: 600, fontSize: 11 }}
            />
          );
        },
      },
      {
        label: 'Actions',
        id: 'actions',
        type: 'callback',
        viewRender: (row) => (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="contained"
              size="small"
              color="primary"
              style={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 6,
                padding: '4px 10px',
              }}
              startIcon={<VisibilityIcon fontSize="small" />}
              onClick={() => handleViewDealerDetails(row)}
            >
              Details
            </Button>
            <Button
              variant="outlined"
              size="small"
              style={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 6,
                padding: '4px 10px',
                color: '#00a65a',
                borderColor: '#00a65a',
              }}
              startIcon={<AccountBalanceWalletIcon fontSize="small" />}
              onClick={() => handleOpenRecharge(getDealerId(row))}
            >
              Recharge
            </Button>
          </div>
        ),
      },
    ],
    [handleViewDealerDetails]
  );

  return (
    <div className={styles.contentWrapper}>
      <Loader isOpen={loading} />

      {/* Page Header */}
      <div className={styles.headerSec}>
        <div className={styles.titleArea}>
          <div>
            <h2 className={styles.headerTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <HandshakeIcon style={{ color: 'var(--primary-color)' }} />
              <span>DEALERS</span>
            </h2>
            <p className={styles.headerSubtitle}>
              Manage dealer accounts, wallet balances, and view transaction details
            </p>
          </div>
        </div>

        <div className={styles.headerActions}>
          <Tooltip title="Refresh dealers">
            <IconButton
              onClick={fetchDealers}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            style={{
              backgroundColor: '#00a65a',
              color: '#ffffff',
              fontWeight: 'bold',
            }}
            className={styles.actionBtn}
            startIcon={<AccountBalanceWalletIcon />}
            onClick={() => handleOpenRecharge('')}
          >
            Recharge Wallet
          </Button>
        </div>
      </div>

      {/* Top Stat Overview Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIconBox} style={{ backgroundColor: '#eff6ff', color: '#3b82f6' }}>
            <PeopleIcon />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Total Dealers</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIconBox} style={{ backgroundColor: '#f0fdf4', color: '#16a34a' }}>
            <CheckCircleIcon />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Active Dealers</span>
            <span className={styles.statValue}>{stats.activeCount}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIconBox}
            style={{
              backgroundColor: stats.totalBal < 0 ? '#fef2f2' : '#f0fdf4',
              color: stats.totalBal < 0 ? '#dc2626' : '#00a65a',
            }}
          >
            <AccountBalanceWalletIcon />
          </div>
          <div className={styles.statContent}>
            <span className={styles.statLabel}>Total Credit Balance</span>
            <span
              className={styles.statValue}
              style={{ color: stats.totalBal < 0 ? '#dc2626' : '#00a65a' }}
            >
              {stats.totalBal < 0 ? '-' : ''}₹
              {Math.abs(stats.totalBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className={styles.filterCard}>
        <div className={styles.searchSec}>
          <SearchIcon style={{ color: '#94a3b8' }} fontSize="small" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            className={styles.searchInput}
            placeholder="Search dealer by name, ID, phone..."
          />
        </div>

        <div className={styles.filterControls}>
          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>Status</span>
            <TextField
              select
              size="small"
              variant="outlined"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              style={{ minWidth: 120 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </div>

          <div className={styles.filterItem}>
            <span className={styles.filterLabel}>Type</span>
            <TextField
              select
              size="small"
              variant="outlined"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(0);
              }}
              style={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="meezan">Meezan</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
          </div>
        </div>
      </div>

      {/* Dealers Data Table */}
      {paginatedDealers && paginatedDealers.length > 0 ? (
        <Paper className={styles.tablePaper}>
          <DataTable
            columns={columns}
            rows={paginatedDealers}
            rowClickListener={handleViewDealerDetails}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredDealers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <Info
          title="No dealers found"
          content={
            searchText || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'No dealers matched your search or filter criteria.'
              : 'There are currently no dealers available.'
          }
        />
      )}

      {/* Wallet Recharge Dialog Popup */}
      <WalletRechargePopup
        open={rechargePopupOpen}
        onClose={() => setRechargePopupOpen(false)}
        dealers={dealers}
        initialDealerId={rechargeTargetDealerId}
        onSuccess={() => {
          fetchDealers();
        }}
      />
    </div>
  );
}

export default withConsoleBase(DealersPage);
